import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSpinner,
  FaTimes,
  FaCheck,
  FaTrash,
  FaExclamationCircle,
  FaServer,
  FaUsers,
  FaCalendarAlt,
  FaCode,
  FaEnvelope,
  FaPhone,
  FaExclamationTriangle,
} from "react-icons/fa";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { AuthContext } from "../context/AuthContext";
import { API_BASE_URL } from "../environment";
import { API } from "../common/Constants";
import { SEO_CONSTANTS } from "../common/SEOConstants";
import { analyticsService } from "../services/analyticsService";
import "../styles/dashboard.css";
import "../styles/welcome-notification.css";
import "../styles/feature-dashboard.css";
import { FaRepeat } from "react-icons/fa6";

// Custom confirmation modal component
const ConfirmationModal = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  modalType,
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="confirmation-modal">
        <div
          className={`modal-header ${
            modalType === "deploy"
              ? "modal-header-deploy"
              : "modal-header-undeploy"
          }`}
        >
          <h3>{title}</h3>
          <button className="close-button" onClick={onCancel}>
            <FaTimes />
          </button>
        </div>
        <div className="modal-body">
          <div className="modal-icon">
            {modalType === "deploy" ? <FaServer /> : <FaExclamationTriangle />}
          </div>
          <p>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            className={`btn-confirm ${
              modalType === "deploy"
                ? "btn-confirm-deploy"
                : "btn-confirm-undeploy"
            }`}
            onClick={onConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const {
    user,
    isAuthenticated,
    loading: authLoading,
  } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [tenantData, setTenantData] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState("");
  const [undeployLoading, setUndeployLoading] = useState(false);
  const [undeployError, setUndeployError] = useState("");
  const [undeploySuccess, setUndeploySuccess] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalType, setModalType] = useState(""); // "deploy" or "undeploy"

  // Features state
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [deploymentStatus, setDeploymentStatus] = useState("idle"); // idle, deploying, success, error
  const [deploymentError, setDeploymentError] = useState("");

  // Selection helpers
  const [selectAll, setSelectAll] = useState(false);
  const [selectPrimary, setSelectPrimary] = useState(false);
  const [selectMiddle, setSelectMiddle] = useState(false);
  const [selectSecondary, setSelectSecondary] = useState(false);
  const [selectSeniorSecondary, setSelectSeniorSecondary] = useState(false);

  const navigate = useNavigate();

  // NCERT Courses data
  const ncertCourses = [
    // Primary (1-5)
    { id: "1", name: "Class 1", grade: 1 },
    { id: "2", name: "Class 2", grade: 2 },
    { id: "3", name: "Class 3", grade: 3 },
    { id: "4", name: "Class 4", grade: 4 },
    { id: "5", name: "Class 5", grade: 5 },

    // Middle (6-8)
    { id: "6", name: "Class 6", grade: 6 },
    { id: "7", name: "Class 7", grade: 7 },
    { id: "8", name: "Class 8", grade: 8 },

    // Secondary (9-10)
    { id: "9", name: "Class 9", grade: 9 },
    { id: "10", name: "Class 10", grade: 10 },

    // Senior Secondary (11-12)
    { id: "11", name: "Class 11", grade: 11 },
    { id: "12", name: "Class 12", grade: 12 },
  ];

  useEffect(() => {
    // Check if user is authenticated
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
      return;
    }

    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Fetch tenant data if user is authenticated
    if (isAuthenticated && user?.id) {
      fetchTenantDetails();
      
      // Track dashboard visit with user role information
      analyticsService.trackEvent(
        'Dashboard',
        'Visit',
        `User: ${user.id}`,
        1
      );
    }

    // Simulate loading user data
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [navigate, isAuthenticated, authLoading, user]);

  // Fetch tenant details from the API
  const fetchTenantDetails = async () => {
    setTenantLoading(true);
    setTenantError("");

    try {
      const response = await axios.get(
        `${API_BASE_URL}${API.GET_TENANT.replace(":userId", user.id)}`,
        {
          headers: {
            Authorization: `${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setTenantData(response.data);
        console.log("Tenant data fetched:", response.data);

        // Reset the deployment/undeployment status when tenant status changes
        const isActiveChanged =
          tenantData && tenantData.isActive !== response.data.isActive;

        if (isActiveChanged) {
          console.log("Tenant active status changed:", response.data.isActive);
          // Reset deployment states if status changed
          setDeploymentStatus("idle");
          setUndeploySuccess(false);
          setUndeployError("");
          setDeploymentError("");
        }

        // If tenant has NCERT courses, set them as selected
        if (response.data.features) {
          const ncertFeature = response.data.features.find(
            (feature) => feature.name.toLowerCase() === "ncertcourses"
          );

          if (ncertFeature) {
            setSelectedFeatures((prev) =>
              prev.includes("ncert-courses") ? prev : [...prev, "ncert-courses"]
            );

            if (ncertFeature.values && ncertFeature.values.length > 0) {
              setSelectedCourses(ncertFeature.values);
              updateFilterButtonsState(ncertFeature.values);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching tenant details:", error);
      setTenantError(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch tenant details. Please try again."
      );
    } finally {
      setTenantLoading(false);
    }
  };

  // Handle undeploying tenant
  const handleUndeployTenant = async () => {
    setModalType("undeploy");
    setShowConfirmModal(true);
  };

  // Actual undeploy function after confirmation
  const confirmUndeploy = async () => {
    setShowConfirmModal(false);
    setUndeployLoading(true);
    setUndeployError("");
    setUndeploySuccess(false);

    try {
      const response = await axios.delete(
        `${API_BASE_URL}${API.UNDEPLOY_USER_TENANT.replace(
          ":userId",
          user.id
        )}`,
        {
          headers: {
            Authorization: `${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (
        response.status === 200 ||
        response.status === 202 ||
        response.status === 204
      ) {
        setUndeploySuccess(true);
        // Refetch tenant data after undeployment
        setTimeout(() => {
          fetchTenantDetails();
        }, 4000);
      } else {
        throw new Error("Undeployment failed. Please try again.");
      }
    } catch (error) {
      console.error("Undeployment error:", error);
      setUndeployError(
        error.response?.data?.message ||
          error.message ||
          "An error occurred during undeployment. Please try again."
      );
    } finally {
      setUndeployLoading(false);
    }
  };

  // Toggle feature selection
  const toggleFeature = (featureId) => {
    const isSelected = !selectedFeatures.includes(featureId);
    
    if (!isSelected) {
      setSelectedFeatures(selectedFeatures.filter((id) => id !== featureId));
      // If we're unselecting NCERT courses, clear the selected courses
      if (featureId === "ncert-courses") {
        setSelectedCourses([]);
      }
    } else {
      setSelectedFeatures([...selectedFeatures, featureId]);
    }
    
    // Track the feature selection/deselection
    analyticsService.trackEvent(
      'Dashboard',
      isSelected ? 'Feature Selected' : 'Feature Deselected',
      `Feature: ${featureId}`,
      isSelected ? 1 : 0
    );
  };

  // Toggle course selection
  const toggleCourse = (courseId) => {
    if (selectedCourses.includes(courseId)) {
      setSelectedCourses(selectedCourses.filter((id) => id !== courseId));

      // Update filter buttons state
      updateFilterButtonsState(selectedCourses.filter((id) => id !== courseId));
    } else {
      setSelectedCourses([...selectedCourses, courseId]);

      // Update filter buttons state
      updateFilterButtonsState([...selectedCourses, courseId]);
    }
  };



  // Helper to update filter button states based on selected courses
  const updateFilterButtonsState = (courses = selectedCourses) => {
    const primaryGrades = [1, 2, 3, 4, 5];
    const middleGrades = [6, 7, 8];
    const secondaryGrades = [9, 10];
    const seniorSecondaryGrades = [11, 12];

    // Get selected course objects
    const selectedCourseObjects = ncertCourses.filter((course) =>
      courses.includes(course.id)
    );

    // Check if all courses of each grade category are selected
    const allPrimaryCourses = ncertCourses.filter((course) =>
      primaryGrades.includes(course.grade)
    );
    const allMiddleCourses = ncertCourses.filter((course) =>
      middleGrades.includes(course.grade)
    );
    const allSecondaryCourses = ncertCourses.filter((course) =>
      secondaryGrades.includes(course.grade)
    );
    const allSeniorSecondaryCourses = ncertCourses.filter((course) =>
      seniorSecondaryGrades.includes(course.grade)
    );

    const selectedPrimaryCourses = selectedCourseObjects.filter((course) =>
      primaryGrades.includes(course.grade)
    );
    const selectedMiddleCourses = selectedCourseObjects.filter((course) =>
      middleGrades.includes(course.grade)
    );
    const selectedSecondaryCourses = selectedCourseObjects.filter((course) =>
      secondaryGrades.includes(course.grade)
    );
    const selectedSeniorSecondaryCourses = selectedCourseObjects.filter(
      (course) => seniorSecondaryGrades.includes(course.grade)
    );

    setSelectPrimary(
      selectedPrimaryCourses.length === allPrimaryCourses.length
    );
    setSelectMiddle(selectedMiddleCourses.length === allMiddleCourses.length);
    setSelectSecondary(
      selectedSecondaryCourses.length === allSecondaryCourses.length
    );
    setSelectSeniorSecondary(
      selectedSeniorSecondaryCourses.length === allSeniorSecondaryCourses.length
    );

    // Check if all courses are selected
    setSelectAll(courses.length === ncertCourses.length);
  };

  // Select all courses
  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all
      setSelectedCourses([]);
      setSelectAll(false);
      setSelectPrimary(false);
      setSelectMiddle(false);
      setSelectSecondary(false);
      setSelectSeniorSecondary(false);
    } else {
      // Select all
      setSelectedCourses(ncertCourses.map((course) => course.id));
      setSelectAll(true);
      setSelectPrimary(true);
      setSelectMiddle(true);
      setSelectSecondary(true);
      setSelectSeniorSecondary(true);
    }
  };

  // Select primary courses (classes 1-5)
  const handleSelectPrimary = () => {
    const primaryCourseIds = ncertCourses
      .filter((course) => [1, 2, 3, 4, 5].includes(course.grade))
      .map((course) => course.id);

    if (selectPrimary) {
      // Deselect primary courses
      setSelectedCourses(
        selectedCourses.filter((id) => !primaryCourseIds.includes(id))
      );
      setSelectPrimary(false);
      setSelectAll(false);
    } else {
      // Add primary courses to selection
      const newSelection = [
        ...new Set([...selectedCourses, ...primaryCourseIds]),
      ];
      setSelectedCourses(newSelection);
      setSelectPrimary(true);

      // Check if all courses are now selected
      if (newSelection.length === ncertCourses.length) {
        setSelectAll(true);
      }

      // Update other filter buttons
      updateFilterButtonsState(newSelection);
    }
  };

  // Select middle school courses (classes 6-8)
  const handleSelectMiddle = () => {
    const middleCourseIds = ncertCourses
      .filter((course) => [6, 7, 8].includes(course.grade))
      .map((course) => course.id);

    if (selectMiddle) {
      // Deselect middle courses
      setSelectedCourses(
        selectedCourses.filter((id) => !middleCourseIds.includes(id))
      );
      setSelectMiddle(false);
      setSelectAll(false);
    } else {
      // Add middle courses to selection
      const newSelection = [
        ...new Set([...selectedCourses, ...middleCourseIds]),
      ];
      setSelectedCourses(newSelection);
      setSelectMiddle(true);

      // Check if all courses are now selected
      if (newSelection.length === ncertCourses.length) {
        setSelectAll(true);
      }

      // Update other filter buttons
      updateFilterButtonsState(newSelection);
    }
  };

  // Select secondary courses (classes 9-10)
  const handleSelectSecondary = () => {
    const secondaryCourseIds = ncertCourses
      .filter((course) => [9, 10].includes(course.grade))
      .map((course) => course.id);

    if (selectSecondary) {
      // Deselect secondary courses
      setSelectedCourses(
        selectedCourses.filter((id) => !secondaryCourseIds.includes(id))
      );
      setSelectSecondary(false);
      setSelectAll(false);
    } else {
      // Add secondary courses to selection
      const newSelection = [
        ...new Set([...selectedCourses, ...secondaryCourseIds]),
      ];
      setSelectedCourses(newSelection);
      setSelectSecondary(true);

      // Check if all courses are now selected
      if (newSelection.length === ncertCourses.length) {
        setSelectAll(true);
      }

      // Update other filter buttons
      updateFilterButtonsState(newSelection);
    }
  };

  // Select senior secondary courses (classes 11-12)
  const handleSelectSeniorSecondary = () => {
    const seniorSecondaryCourseIds = ncertCourses
      .filter((course) => [11, 12].includes(course.grade))
      .map((course) => course.id);

    if (selectSeniorSecondary) {
      // Deselect senior secondary courses
      setSelectedCourses(
        selectedCourses.filter((id) => !seniorSecondaryCourseIds.includes(id))
      );
      setSelectSeniorSecondary(false);
      setSelectAll(false);
    } else {
      // Add senior secondary courses to selection
      const newSelection = [
        ...new Set([...selectedCourses, ...seniorSecondaryCourseIds]),
      ];
      setSelectedCourses(newSelection);
      setSelectSeniorSecondary(true);

      // Check if all courses are now selected
      if (newSelection.length === ncertCourses.length) {
        setSelectAll(true);
      }

      // Update other filter buttons
      updateFilterButtonsState(newSelection);
    }
  };

  // Handle deploying tenant
  const handleDeployTenant = async () => {
    setModalType("deploy");
    setShowConfirmModal(true);
  };

  // Start deployment process
  const startDeployment = async () => {
    setShowConfirmModal(false);
    setDeploymentStatus("deploying");
    setDeploymentError("");

    try {
      const deploymentData = {
        userId: user.id,
        features: [
          {
            name: "ncertCourses",
            values: selectedFeatures.includes("ncert-courses")
              ? selectedCourses
              : [],
          },
        ],
      };

      // Track feature activation event
      analyticsService.trackEvent(
        'Dashboard',
        'Tenant Deployment',
        `Features: ${selectedFeatures.join(', ')}`,
        selectedFeatures.length
      );

      const response = await axios.post(
        `${API_BASE_URL}${API.DEPLOY_USER_TENANT.replace(":userId", user.id)}`,
        deploymentData,
        {
          headers: {
            Authorization: `${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 || response.status === 202) {
        setDeploymentStatus("success");

        // Fetch updated tenant data after successful deployment
        setTimeout(() => {
          fetchTenantDetails();
        }, 4000);
      } else {
        throw new Error("Deployment failed. Please try again.");
      }
    } catch (error) {
      console.error("Deployment error:", error);
      setDeploymentStatus("error");
      setDeploymentError(
        error.response?.data?.message ||
          error.message ||
          "An error occurred during deployment. Please try again."
      );
    }
  };

  if (authLoading || loading) {
    return (
      <div
        className="loading-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <FaSpinner className="spinner" size={40} />
        <p style={{ marginLeft: "10px" }}>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <section className="dashboard-page">
      <Helmet>
        <title>Dashboard - Pathshala Sanchalak</title>
        <meta
          name="description"
          content="Manage your educational institution with Pathshala Sanchalak's comprehensive dashboard. Access student data, faculty management, course scheduling, and more."
        />
        <meta
          name="keywords"
          content={SEO_CONSTANTS.getCombinedKeywords(
            SEO_CONSTANTS.DASHBOARD_KEYWORDS
          )}
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="free-banner">
        Thank you for choosing Pathshala Sanchalak - Your 100% FREE
        comprehensive educational management platform!
      </div>

      <div className="dashboard-container">

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmModal}
          title={
            modalType === "undeploy"
              ? "Confirm Tenant Deactivation"
              : "Confirm Tenant Activation"
          }
          message={
            modalType === "undeploy"
              ? "Are you sure you want to deactivate your tenant? This action cannot be undone and all associated data may be lost."
              : "Are you sure you want to activate your tenant with the selected features? This will set up your institution's environment."
          }
          onConfirm={
            modalType === "undeploy" ? confirmUndeploy : startDeployment
          }
          onCancel={() => setShowConfirmModal(false)}
          modalType={modalType}
        />
        <div className="dashboard-header">
          <h1>Welcome to Your Dashboard, {user?.name || "User"}</h1>
          <p>You are now logged in as {user?.email}</p>
        </div>

        <div className="dashboard-content">
          {tenantLoading ? (
            <div className="loading-container" style={{ padding: "2rem 0" }}>
              <FaSpinner className="spinner" size={30} />
              <p style={{ marginLeft: "10px" }}>
                Loading tenant information...
              </p>
            </div>
          ) : tenantError ? (
            <div
              className="error-container"
              style={{ padding: "2rem 0", color: "red" }}
            >
              <FaExclamationCircle size={30} />
              <p style={{ marginLeft: "10px" }}>{tenantError}</p>
            </div>
          ) : tenantData ? (
            <div className="dashboard-card tenant-details">
              <h2>Your Institution Details</h2>
              <div className="tenant-info">
                <div className="tenant-header">
                  <h3>{tenantData.name || "Academic Institution"}</h3>
                  <div
                    className={`deployment-status-badge ${
                      tenantData.deploymentStatus?.toLowerCase() === "deployed"
                        ? "active"
                        : tenantData.deploymentStatus?.toLowerCase() ===
                          "not_deployed"
                        ? "inactive"
                        : tenantData.deploymentStatus?.toLowerCase() ===
                          "failed"
                        ? "failed"
                        : "unknown"
                    }`}
                  >
                    {tenantData.deploymentStatus?.toLowerCase() ===
                    "deployed" ? (
                      <>
                        <span className="status-dot"></span>
                        <span className="status-text">ACTIVE</span>
                      </>
                    ) : tenantData.deploymentStatus?.toLowerCase() ===
                      "not_deployed" ? (
                      <>
                        <span className="status-dot"></span>
                        <span className="status-text">INACTIVE</span>
                      </>
                    ) : tenantData.deploymentStatus?.toLowerCase() ===
                      "failed" ? (
                      <>
                        <span className="status-dot"></span>
                        <span className="status-text">FAILED</span>
                      </>
                    ) : (
                      <>
                        <span className="status-dot"></span>
                        <span className="status-text">Status Unknown</span>
                      </>
                    )}
                  </div>
                </div>

                {tenantData.description && (
                  <p className="tenant-description">{tenantData.description}</p>
                )}

                <h4 style={{ marginBottom: "-20px" }}>Tenant Information</h4>

                <div className="tenant-details-grid">
                  <div className="tenant-detail-item">
                    <FaServer />
                    <div>
                      <strong>Tenant Code:</strong>
                      <p>{tenantData.code || "N/A"}</p>
                    </div>
                  </div>

                  <div className="tenant-detail-item">
                    <FaUsers />
                    <div>
                      <strong>Owner:</strong>
                      <p>{tenantData.owner || user?.name || "N/A"}</p>
                    </div>
                  </div>

                  {tenantData.domain && (
                    <div className="tenant-detail-item">
                      <FaCode />
                      <div>
                        <strong>Admin Portal:</strong>
                        <p>{tenantData.domain}</p>
                      </div>
                    </div>
                  )}

                  {tenantData.createdAt && (
                    <div className="tenant-detail-item">
                      <FaCalendarAlt />
                      <div>
                        <strong>Created On:</strong>
                        <p>{new Date(tenantData.createdAt).toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>

                {tenantData.contactInfo && (
                  <div
                    className="contact-info"
                    style={{ marginTop: "20px", paddingTop: "5px" }}
                  >
                    <h4 style={{ marginBottom: "-20px" }}>
                      Contact Information
                    </h4>
                    <div
                      className="tenant-details-grid"
                      style={{ margin: "0", gap: "10px" }}
                    >
                      <div
                        className="tenant-detail-item"
                        style={{ padding: "8px" }}
                      >
                        <FaEnvelope />
                        <div>
                          <strong>Email:</strong>
                          <p style={{ marginTop: "2px" }}>
                            {tenantData.contactInfo.email || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div
                        className="tenant-detail-item"
                        style={{ padding: "8px" }}
                      >
                        <FaPhone />
                        <div>
                          <strong>Phone:</strong>
                          <p style={{ marginTop: "2px" }}>
                            {tenantData.contactInfo.phone || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {tenantData.features && tenantData.features.length > 0 && (
                  <div
                    className="active-features"
                    style={{ marginTop: "10px", paddingTop: "10px" }}
                  >
                    <h4
                      style={{
                        marginBottom: "5px",
                        fontSize: "0.95rem",
                        display: "inline-block",
                      }}
                    >
                      Active Features:
                    </h4>
                    <ul style={{ marginTop: "5px" }}>
                      {tenantData.features.map((feature, index) => (
                        <li key={index}>
                          <strong>{feature.name}:</strong>
                          {feature.enabled !== undefined ? (
                            feature.enabled ? (
                              " Enabled"
                            ) : (
                              " Disabled"
                            )
                          ) : feature.values ? (
                            <ul className="feature-values">
                              {feature.values.map((value, valueIndex) => (
                                <li key={valueIndex}>{value}</li>
                              ))}
                            </ul>
                          ) : (
                            " N/A"
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Feature selection section for inactive tenants */}
                {!tenantData.isActive && (
                  <div className="feature-selection">
                    <h3>Select Features to Deploy</h3>
                    <div className="feature-category">
                      <div className="feature-item">
                        <input
                          type="checkbox"
                          id="ncert-courses"
                          checked={selectedFeatures.includes("ncert-courses")}
                          onChange={() => toggleFeature("ncert-courses")}
                        />
                        <label htmlFor="ncert-courses">NCERT Courses</label>

                        {selectedFeatures.includes("ncert-courses") && (
                          <div className="feature-details">
                            <p>
                              Select the NCERT courses you want to pre-load in
                              your application:
                            </p>
                            <div className="course-selection">
                              <div className="grade-filters">
                                <button
                                  className={`grade-filter-btn ${
                                    selectAll ? "active" : ""
                                  }`}
                                  onClick={handleSelectAll}
                                >
                                  All Classes
                                </button>
                                <button
                                  className={`grade-filter-btn ${
                                    selectPrimary ? "active" : ""
                                  }`}
                                  onClick={handleSelectPrimary}
                                >
                                  Primary (1-5)
                                </button>
                                <button
                                  className={`grade-filter-btn ${
                                    selectMiddle ? "active" : ""
                                  }`}
                                  onClick={handleSelectMiddle}
                                >
                                  Middle (6-8)
                                </button>
                                <button
                                  className={`grade-filter-btn ${
                                    selectSecondary ? "active" : ""
                                  }`}
                                  onClick={handleSelectSecondary}
                                >
                                  Secondary (9-10)
                                </button>
                                <button
                                  className={`grade-filter-btn ${
                                    selectSeniorSecondary ? "active" : ""
                                  }`}
                                  onClick={handleSelectSeniorSecondary}
                                >
                                  Senior Secondary (11-12)
                                </button>
                              </div>

                              <div className="course-grid">
                                {ncertCourses.map((course) => (
                                  <div key={course.id} className="course-item">
                                    <input
                                      type="checkbox"
                                      id={`course-${course.id}`}
                                      checked={selectedCourses.includes(
                                        course.id
                                      )}
                                      onChange={() => toggleCourse(course.id)}
                                    />
                                    <label htmlFor={`course-${course.id}`}>
                                      {course.name}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* <div className="feature-item disabled">
                        <input
                          type="checkbox"
                          id="assessment-engine"
                          disabled
                        />
                        <label htmlFor="assessment-engine">
                          Live Classroom (Coming Soon)
                        </label>
                      </div>
                      <div className="feature-item disabled">
                        <input
                          type="checkbox"
                          id="analytics-dashboard"
                          disabled
                        />
                        <label htmlFor="analytics-dashboard">
                          Attendance Tracking (Coming Soon)
                        </label>
                      </div> */}
                    </div>
                  </div>
                )}

                {/* Conditionally show action buttons based on isActive */}
                <div className="tenant-actions">
                  {tenantData.isActive ? (
                    <div className="action-buttons">
                      <button
                        className="refresh-tenant-btn"
                        onClick={fetchTenantDetails}
                        disabled={tenantLoading}
                      >
                        {tenantLoading ? (
                          <>
                            <FaSpinner className="spinner" /> Refreshing...
                          </>
                        ) : (
                          <>
                            <FaRepeat /> Refresh Tenant
                          </>
                        )}
                      </button>

                      <button
                        className="undeploy-tenant-btn"
                        onClick={handleUndeployTenant}
                        disabled={undeployLoading}
                      >
                        {undeployLoading ? (
                          <>
                            <FaSpinner className="spinner" /> Undeploying...
                          </>
                        ) : (
                          <>
                            <FaTrash /> Deactivate Tenant
                          </>
                        )}
                      </button>

                      {undeployError && (
                        <p className="undeploy-error">{undeployError}</p>
                      )}

                      {undeploySuccess && (
                        <p className="undeploy-success">
                          <FaCheck /> Your tenant has been successfully
                          deactivated.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="deployment-actions">
                      <button
                        className="start-deployment-btn"
                        disabled={
                          deploymentStatus === "deploying" ||
                          deploymentStatus === "success" ||
                          (selectedFeatures.includes("ncert-courses") &&
                            selectedCourses.length === 0)
                        }
                        onClick={handleDeployTenant}
                      >
                        {deploymentStatus === "deploying" ? (
                          <>
                            <FaSpinner className="spinner" /> Deploying...
                          </>
                        ) : deploymentStatus === "success" ? (
                          <>
                            <FaCheck /> Tenant Activation Request Successful!
                          </>
                        ) : (
                          "Activate Tenant"
                        )}
                      </button>

                      {deploymentStatus === "success" && (
                        <button
                          className="refresh-tenant-btn"
                          onClick={fetchTenantDetails}
                          disabled={tenantLoading}
                        >
                          {tenantLoading ? (
                            <>
                              <FaSpinner className="spinner" /> Refreshing...
                            </>
                          ) : (
                            <>
                              <FaServer /> Refresh Tenant
                            </>
                          )}
                        </button>
                      )}

                      {deploymentError && (
                        <p className="deployment-error">{deploymentError}</p>
                      )}
                      {deploymentStatus === "success" && (
                        <p className="deployment-success">
                          Request submitted successfully! You will receive an
                          email shortly once the tenant activation is complete
                          with further instructions.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="dashboard-card premium-features">
              <h2>Feature Selection</h2>
              <p>
                Select the features you would like to enable for your
                institution:
              </p>

              <div className="features-container">
                <div className="feature-category">
                  <h3>Pre-loaded Content</h3>
                  <div className="feature-item">
                    <input
                      type="checkbox"
                      id="ncert-courses"
                      checked={selectedFeatures.includes("ncert-courses")}
                      onChange={() => toggleFeature("ncert-courses")}
                    />
                    <label htmlFor="ncert-courses">NCERT Courses</label>

                    {selectedFeatures.includes("ncert-courses") && (
                      <div className="feature-details">
                        <p>
                          Select the NCERT courses you want to pre-load in your
                          application:
                        </p>
                        <div className="course-selection">
                          <div className="grade-filters">
                            <button
                              className={`grade-filter-btn ${
                                selectAll ? "active" : ""
                              }`}
                              onClick={handleSelectAll}
                            >
                              All Classes
                            </button>
                            <button
                              className={`grade-filter-btn ${
                                selectPrimary ? "active" : ""
                              }`}
                              onClick={handleSelectPrimary}
                            >
                              Primary (1-5)
                            </button>
                            <button
                              className={`grade-filter-btn ${
                                selectMiddle ? "active" : ""
                              }`}
                              onClick={handleSelectMiddle}
                            >
                              Middle (6-8)
                            </button>
                            <button
                              className={`grade-filter-btn ${
                                selectSecondary ? "active" : ""
                              }`}
                              onClick={handleSelectSecondary}
                            >
                              Secondary (9-10)
                            </button>
                            <button
                              className={`grade-filter-btn ${
                                selectSeniorSecondary ? "active" : ""
                              }`}
                              onClick={handleSelectSeniorSecondary}
                            >
                              Senior Secondary (11-12)
                            </button>
                          </div>

                          <div className="course-grid">
                            {ncertCourses.map((course) => (
                              <div key={course.id} className="course-item">
                                <input
                                  type="checkbox"
                                  id={`course-${course.id}`}
                                  checked={selectedCourses.includes(course.id)}
                                  onChange={() => toggleCourse(course.id)}
                                />
                                <label htmlFor={`course-${course.id}`}>
                                  {course.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Placeholder for future features */}
                  {/* <div className="feature-item disabled">
                    <input type="checkbox" id="custom-curriculum" disabled />
                    <label htmlFor="custom-curriculum">Custom Curriculum (Coming Soon)</label>
                  </div> */}
                </div>

                <div className="feature-category">
                  <h3>Upcoming Features</h3>
                  <div className="feature-item disabled">
                    <input type="checkbox" id="assessment-engine" disabled />
                    <label htmlFor="assessment-engine">
                      Live Classroom (Coming Soon)
                    </label>
                  </div>
                  <div className="feature-item disabled">
                    <input type="checkbox" id="analytics-dashboard" disabled />
                    <label htmlFor="analytics-dashboard">
                      Attendance Tracking (Coming Soon)
                    </label>
                  </div>
                </div>
              </div>

              <div className="deployment-actions">
                <button
                  className="start-deployment-btn"
                  disabled={
                    deploymentStatus === "deploying" ||
                    deploymentStatus === "success" ||
                    (selectedFeatures.includes("ncert-courses") &&
                      selectedCourses.length === 0)
                  }
                  onClick={handleDeployTenant}
                >
                  {deploymentStatus === "deploying" ? (
                    <>
                      <FaSpinner className="spinner" /> Deploying...
                    </>
                  ) : deploymentStatus === "success" ? (
                    <>
                      <FaCheck /> Tenant Activation Request Successful!
                    </>
                  ) : (
                    "Activate Tenant"
                  )}
                </button>

                {deploymentStatus === "success" && (
                  <button
                    className="refresh-tenant-btn"
                    onClick={fetchTenantDetails}
                    disabled={tenantLoading}
                  >
                    {tenantLoading ? (
                      <>
                        <FaSpinner className="spinner" /> Refreshing...
                      </>
                    ) : (
                      <>
                        <FaServer /> Refresh Tenant
                      </>
                    )}
                  </button>
                )}

                {deploymentError && (
                  <p className="deployment-error">{deploymentError}</p>
                )}
                {deploymentStatus === "success" && (
                  <p className="deployment-success">
                    Request submitted successfully! You will receive an email
                    shortly once the tenant activation is complete with further
                    instructions.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="dashboard-card">
            <h2>Next Steps</h2>
            <p>After deployment completes:</p>
            <ul>
              <li>Complete your institution profile</li>
              <li>Configure your academic year settings</li>
              <li>Set up additional courses and classes</li>
              <li>Onboard faculty and staff members</li>
              <li>Onboard students</li>
              <li>Start using the platform!</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Dashboard;
